/**
 * ScriptWriterNode — Canvas V2
 *
 * Displays script content, model variant tabs (Gemini/Claude/GPT),
 * structured screenplay rendering, conversation history, and review history.
 * Supports editing the screenplay via RichTextBox.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState, type WheelEvent as ReactWheelEvent } from 'react'
import { Handle, Position, useReactFlow, type NodeProps } from 'reactflow'
import { MessageSquarePlus, Palette } from 'lucide-react'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { cn } from '../../../../lib/utils'
import { executeNode, cancelNodeExecution, InsufficientSparksError, createDebug, type ScriptWriterNodeData, type NodeStatus } from '@ponpon/api-client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useClickLock } from '../../../../hooks/useClickLock'
import { useCanvasOptional } from '../../CanvasContext'
import { useSetHoveredNodeId } from '../../NodeHoverContext'
import type { ReactFlowCanvasEdgeData, ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  useNodeActions,
  computeBorderStyle,
  RichTextBox,
  CREATIVE_TEXT_MAX_LENGTH,
  type ToolbarAction,
  type RichTextBoxHandle,
  type RichTextBoxState,
} from '../shared'
import { NODE_BG_COLORS, NODE_BG_SWATCH_COLORS, resolveNodeBgColor } from '../shared/nodeBgColors'
import { parseScreenplayText } from './parseScreenplayText'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { getScriptModelLabel } from '../../../../constants/modelAliases'

const streamLog = createDebug('dw:script-streaming:render')
const errLog = createDebug('dw:script-writer:error')

// ─── Constants ────────────────────────────────────────────────────────────────

const NODE_WIDTH = 920
// Must match the height in schemas/nodes/scriptWriter.json. Capping the
// outer node here keeps FE-measured dimensions ≤ schema default so the
// layout engine and Director never under-reserve space for downstream nodes.
const NODE_MAX_HEIGHT = 620

const agentColor = AGENT_COLORS.scriptwriter

// ─── Types ────────────────────────────────────────────────────────────────────

export type { ScriptWriterNodeData }

type ModelKey = ScriptWriterNodeData['in_active_model'] & string
type VoiceRole = NonNullable<ScriptWriterNodeData['out_voice_roles']>[number]
type VoiceProfile = NonNullable<ScriptWriterNodeData['out_voice_profiles']>[number]

type ScriptNodeStatus = NodeStatus | 'needs-input'

type StatusAnimation = false | 'pulse' | 'spin' | 'spin-slow'

const STATUS_INDICATOR: Record<ScriptNodeStatus, { color: string; label: string; animate: StatusAnimation }> = {
  idle:                      { color: 'bg-text-muted/30', label: 'Ready',      animate: false       },
  pending_execution:         { color: 'bg-warning',       label: 'Queued',     animate: 'spin-slow' },
  pending_cost_confirmation: { color: 'bg-warning',       label: 'Pending approval',    animate: false       },
  running:                   { color: 'bg-primary',       label: 'Generating', animate: 'spin'      },
  completed:                 { color: 'bg-success',       label: 'Done',       animate: false       },
  error:                     { color: 'bg-destructive',   label: 'Error',      animate: false       },
  'needs-input':             { color: 'bg-amber-500',     label: 'Needs input', animate: 'pulse'    },
}

interface ModelTabInfo {
  key: ModelKey
  label: string
  hasData: boolean
  hasError: boolean
}

const MODEL_LABELS: Record<ModelKey, string> = {
  gemini: getScriptModelLabel('gemini'),
  claude: getScriptModelLabel('claude'),
  gpt: getScriptModelLabel('gpt'),
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const safeString = (value: string | number | boolean | object | null | undefined): string => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try { return JSON.stringify(value) } catch { return String(value) }
}

function toScriptNodeStatus(value: string | undefined): ScriptNodeStatus {
  const normalized = value?.toLowerCase()
  return normalized === 'pending_execution' ||
    normalized === 'pending_cost_confirmation' ||
    normalized === 'running' ||
    normalized === 'completed' ||
    normalized === 'error' ||
    normalized === 'idle' ||
    normalized === 'needs-input'
    ? normalized
    : 'idle'
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const ScriptIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
)

const PencilIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
)

const CheckIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
)

const CopyIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
  </svg>
)

const TrashIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
)

// ─── Structured screenplay rendering ──────────────────────────────────────────

interface ScreenplayEntry {
  type: string
  character?: string
  emotion?: string
  voice_role_id?: string
  text: string
}

interface ScreenplayScene {
  scene_number: number
  location: string
  time_of_day?: string
  interior_exterior?: string
  characters?: string[]
  entries: ScreenplayEntry[]
}

const formatVoiceRoleKind = (kind: string | undefined): string => {
  if (!kind) return 'voice'
  return kind.replace(/_/g, ' ')
}

const SceneEntry = ({
  entry,
  voiceRolesById,
}: {
  entry: ScreenplayEntry
  voiceRolesById: Map<string, VoiceRole>
}) => {
  if (entry.type === 'dialogue' || entry.type === 'os') {
    return (
      <div className="rounded-lg bg-amber-500/10 p-2">
        {entry.character && (
          <p className="text-[10px] font-bold text-foreground/90">
            {entry.character}
            {entry.emotion && (
              <span className="ml-1.5 font-normal text-amber-400/70">({entry.emotion})</span>
            )}
            {entry.type === 'os' && (
              <span className="ml-1.5 text-[9px] font-normal text-text-muted">(O.S.)</span>
            )}
          </p>
        )}
        <p className="text-[10px] text-foreground/80 leading-relaxed mt-0.5">
          &ldquo;{entry.text}&rdquo;
        </p>
      </div>
    )
  }

  if (entry.type === 'voiceover') {
    const role = entry.voice_role_id ? voiceRolesById.get(entry.voice_role_id) : undefined
    const displayName = role?.label ?? entry.character ?? entry.voice_role_id ?? 'Voiceover'

    return (
      <div className="rounded-lg border border-primary/20 bg-primary/10 p-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="text-xxs font-bold text-foreground/90">{displayName}</p>
          <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-xxs uppercase tracking-wider text-primary/80">
            {formatVoiceRoleKind(role?.kind)}
          </span>
          {entry.voice_role_id && (
            <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
              {entry.voice_role_id}
            </span>
          )}
          {entry.emotion && (
            <span className="text-xxs font-normal text-primary/70">({entry.emotion})</span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-foreground/80 leading-relaxed italic">
          &ldquo;{entry.text}&rdquo;
        </p>
      </div>
    )
  }

  if (entry.type === 'sfx') {
    return (
      <div className="flex items-start gap-1.5">
        <span className="inline-block text-[8px] px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 font-medium uppercase tracking-wider flex-shrink-0 mt-px">
          SFX
        </span>
        <p className="text-[10px] text-foreground/60 leading-relaxed">{entry.text}</p>
      </div>
    )
  }

  if (entry.type === 'music') {
    return (
      <div className="flex items-start gap-1.5">
        <span className="inline-block text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium uppercase tracking-wider flex-shrink-0 mt-px">
          Music
        </span>
        <p className="text-[10px] text-foreground/60 leading-relaxed">{entry.text}</p>
      </div>
    )
  }

  // action (default)
  return (
    <p className="text-[10px] text-foreground/70 leading-relaxed">{entry.text}</p>
  )
}

const SceneCard = ({
  scene,
  voiceRolesById,
  isStreaming,
}: {
  scene: ScreenplayScene
  voiceRolesById: Map<string, VoiceRole>
  isStreaming?: boolean
}) => {
  let header = `SCENE ${scene.scene_number}`
  if (scene.interior_exterior) header += ` — ${scene.interior_exterior}`
  if (scene.location) header += `. ${scene.location}`
  if (scene.time_of_day) header += ` — ${scene.time_of_day}`

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold text-foreground/90 uppercase tracking-wider">{header}</p>
      {scene.entries.map((entry, i) => (
        <SceneEntry key={i} entry={entry} voiceRolesById={voiceRolesById} />
      ))}
      {isStreaming && (
        <span className="inline-block text-foreground/60 animate-[blink_1s_step-end_infinite]">
          &#9608;
        </span>
      )}
    </div>
  )
}

const VoiceRolesPanel = memo(function VoiceRolesPanel({
  voiceRoles,
  voiceProfilesByName,
}: {
  voiceRoles: VoiceRole[]
  voiceProfilesByName: Map<string, VoiceProfile>
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (voiceRoles.length === 0) return null

  return (
    <div className="border-b border-border-subtle" data-testid="script-voice-roles-panel">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-surface-1 nodrag"
        aria-expanded={isExpanded}
        data-testid="script-voice-roles-toggle"
      >
        <svg className="h-3 w-3 shrink-0 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <polyline points={isExpanded ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
        </svg>
        <span className="shrink-0 text-xs text-text-muted">Narrators</span>
        {!isExpanded && (
          <span className="min-w-0 flex-1 truncate text-xs text-text-secondary">
            {voiceRoles.map((role) => role.label).join(', ')}
          </span>
        )}
        <span className="shrink-0 text-xs text-text-muted">
          {voiceRoles.length}
        </span>
      </button>
      {isExpanded && (
        <div className="max-h-[120px] overflow-y-auto px-4 pb-3 pl-9 text-xs text-text-secondary nodrag nowheel" data-testid="script-voice-roles-expanded">
          {voiceRoles.map((role) => (
            <VoiceRoleRow
              key={role.id}
              role={role}
              profile={voiceProfilesByName.get(role.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
})

const VoiceRoleRow = memo(function VoiceRoleRow({
  role,
  profile,
}: {
  role: VoiceRole
  profile?: VoiceProfile
}) {
  return (
    <div
      className="py-1"
      data-testid="script-voice-role-row"
    >
      <p className="truncate">
        <span className="font-medium text-foreground/80">{role.label}</span>
        <span className="text-text-muted"> - {formatVoiceRoleKind(role.kind)} - {role.id}</span>
      </p>
      {profile?.voice_name && (
        <p className="mt-0.5 truncate text-text-muted">
          Voice profile: {profile.voice_name}
        </p>
      )}
      {role.voice_description && (
        <p className="mt-0.5 line-clamp-2 text-text-muted">
          {role.voice_description}
        </p>
      )}
    </div>
  )
})

// ─── Component ────────────────────────────────────────────────────────────────

export const ScriptWriterNode = memo(function ScriptWriterNode(props: NodeProps<ScriptWriterNodeData>) {
  const { id, data, selected, dragging } = props
  const projectId = useProjectId()
  const showInsufficientSparks = useShowInsufficientSparks()
  // Per-node 1s cooldown so a fast double-tap on Regenerate can't
  // fire /execute-node twice.
  const { isLocked: isRegenLockedKey, run: runRegenerate } = useClickLock(1000)
  const regenerateLocked = isRegenLockedKey(id)
  const {
    in_name: nodeName = '',
    label: _nodeLabel = '',
    in_prompt: prompt = '',
    out_script: script,
    out_script_path: scriptPath,
    in_active_model: activeModel,
    out_active_model: resolvedActiveModel,
    out_gemini: outGemini,
    out_claude: outClaude,
    out_gpt: outGpt,
    out_voice_roles: rawVoiceRoles,
    out_voice_profiles: rawVoiceProfiles,
    in_review_history: rawReviewHistory,
    status = 'idle',
    progressMessage,
    error: rawError,
  } = data

  // YDoc can serialize empty list-valued fields as null; the destructuring
  // default `[]` only applies to `undefined`, so guard against null here.
  const reviewHistory = rawReviewHistory ?? []

  const error = typeof rawError === 'string' ? rawError : rawError ? String(rawError) : undefined

  // NAD logging — record any error signal visible to the renderer so the
  // backend nad-debug.log timeline includes script_writer error states.
  // Fires on every render where any error-related field is non-empty so we can
  // diagnose canvas-vs-backend disagreements.
  useEffect(() => {
    const geminiErr = outGemini?.error
    const claudeErr = outClaude?.error
    const gptErr = outGpt?.error
    if (!error && status !== 'error' && !geminiErr && !claudeErr && !gptErr) return
    errLog({
      node_id: id,
      status,
      error,
      gemini_error: geminiErr ? String(geminiErr) : undefined,
      claude_error: claudeErr ? String(claudeErr) : undefined,
      gpt_error: gptErr ? String(gptErr) : undefined,
    })
  }, [id, status, error, outGemini, outClaude, outGpt])

  // Hooks
  const { setNodes } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()
  const canvas = useCanvasOptional()
  const setHoveredNodeId = useSetHoveredNodeId()
  const { handleDelete, handleDuplicate, handleAddToChat } = useNodeActions({ nodeId: id })
  const richTextBoxRef = useRef<RichTextBoxHandle>(null)

  // Local state
  const [isHovered, setIsHovered] = useState(false)
  const [showReviewHistory, setShowReviewHistory] = useState(false)
  const [localActiveModel, setLocalActiveModel] = useState<ModelKey | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editSessionKey, setEditSessionKey] = useState(0)
  const [feedbackMode, setFeedbackMode] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')
  const [copied, setCopied] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const colorPickerRef = useRef<HTMLDivElement>(null)

  const isRunning = status === 'running'
  const [isCancelling, setIsCancelling] = useState(false)
  const handleCancel = useCallback(() => {
    if (!isRunning || !projectId || isCancelling) return
    setIsCancelling(true)
    cancelNodeExecution(projectId, id)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('cancelNodeExecution failed:', err)
      })
      .finally(() => setIsCancelling(false))
  }, [isRunning, projectId, id, isCancelling])

  // Regenerate screenplay with optional feedback
  const handleRegenerate = useCallback(async () => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation' || !projectId) return

    void runRegenerate(id, async () => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  status: 'running',
                  progressMessage: 'Regenerating script...',
                  error: undefined,
                },
              }
            : n
        )
      )

      try {
        // Write feedback to YDoc before executing
        if (feedbackText.trim()) {
          canvas?.updateNodeData(id, { in_feedback: feedbackText.trim() })
        }
        await executeNode(projectId, id)
        setFeedbackText('')
        setFeedbackMode(false)
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({ balance: err.balance, required: err.required, nodeId: id })
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id
                ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
                : n
            )
          )
          return
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n
          )
        )
      }
    })
  }, [projectId, id, feedbackText, status, setNodes, canvas, runRegenerate, showInsufficientSparks])

  // Model variant state
  const hasVariants = !!(outGemini || outClaude || outGpt)

  const modelTabs: ModelTabInfo[] = useMemo(() => {
    if (!hasVariants) return []
    const models: { key: ModelKey; data: ScriptWriterNodeData['out_gemini'] | null | undefined }[] = [
      { key: 'gemini', data: outGemini },
      { key: 'claude', data: outClaude },
      { key: 'gpt', data: outGpt },
    ]
    return models.map(({ key, data: modelData }) => ({
      key,
      label: MODEL_LABELS[key],
      hasData: !!modelData && !modelData?.error,
      hasError: !!modelData?.error,
    }))
  }, [hasVariants, outGemini, outClaude, outGpt])

  const currentActiveModel: ModelKey | null = useMemo(() => {
    if (!hasVariants) return null
    if (localActiveModel) return localActiveModel
    if (activeModel) return activeModel
    if (resolvedActiveModel) return resolvedActiveModel
    if (outGemini && !outGemini.error) return 'gemini'
    if (outClaude && !outClaude.error) return 'claude'
    if (outGpt && !outGpt.error) return 'gpt'
    return null
  }, [hasVariants, localActiveModel, activeModel, resolvedActiveModel, outGemini, outClaude, outGpt])

  const activeModelData = useMemo(() => {
    if (!hasVariants || !currentActiveModel) return null
    return { gemini: outGemini, claude: outClaude, gpt: outGpt }[currentActiveModel] ?? null
  }, [hasVariants, currentActiveModel, outGemini, outClaude, outGpt])

  const activeScenes: ScreenplayScene[] | null = useMemo(() => {
    const modelData = activeModelData
    if (!modelData || modelData.error) return null
    const scenes = modelData.scenes
    return scenes && scenes.length > 0 ? scenes : null
  }, [activeModelData])

  const activeVoiceRoles: VoiceRole[] = useMemo(() => {
    if (rawVoiceRoles && rawVoiceRoles.length > 0) return rawVoiceRoles
    if (activeModelData && !activeModelData.error && activeModelData.voice_roles?.length) {
      return activeModelData.voice_roles
    }
    return []
  }, [activeModelData, rawVoiceRoles])

  const voiceRolesById = useMemo(
    () => new Map(activeVoiceRoles.map((role) => [role.id, role])),
    [activeVoiceRoles],
  )
  const voiceProfilesByName = useMemo(() => {
    const profiles = rawVoiceProfiles ?? []
    const entries: Array<[string, VoiceProfile]> = []
    for (const profile of profiles) {
      if (profile.name) entries.push([profile.name, profile])
    }
    return new Map(entries)
  }, [rawVoiceProfiles])

  // Parse streaming text into progressive scene cards during generation
  const streamingScenes = useMemo(() => {
    if (!isRunning || !script) return null
    const parsed = parseScreenplayText(safeString(script))
    if (parsed.length > 0) {
      streamLog('streaming nodeId=%s scenes=%d scriptLen=%d', id, parsed.length, safeString(script).length)
    }
    return parsed
  }, [isRunning, script, id])

  // Fallback: parse out_script into scene cards when structured data hasn't arrived yet
  const fallbackScenes = useMemo(() => {
    if (isRunning || activeScenes || !script) return null
    const parsed = parseScreenplayText(safeString(script))
    return parsed.length > 0 ? parsed : null
  }, [isRunning, activeScenes, script])

  // Agent status – normalize to lowercase to handle case-insensitive backend values
  const agentStatus = toScriptNodeStatus(status)

  const indicator = STATUS_INDICATOR[agentStatus] ?? STATUS_INDICATOR.idle

  // Border style
  const borderStyle = computeBorderStyle(!!selected, status, isHovered, agentColor)

  // ── Handlers ──

  /** Prevent wheel events from propagating to the canvas (scroll trapping). */
  const stopWheelPropagation = useCallback((e: ReactWheelEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }, [])

  const handleTabClick = useCallback(
    (modelKey: ModelKey) => {
      if (modelKey === currentActiveModel) return
      setLocalActiveModel(modelKey)
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_active_model: modelKey } } : n
        )
      )
      canvas?.updateNodeData(id, { in_active_model: modelKey })
    },
    [id, canvas, currentActiveModel, setNodes]
  )

  const handleScriptChange = useCallback(
    (state: RichTextBoxState) => {
      const newScript = state.plainText
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, out_script: newScript } } : n
        )
      )
      canvas?.updateNodeData(id, { out_script: newScript })
    },
    [id, setNodes, canvas]
  )

  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_name: newName } } : n
        )
      )
      canvas?.updateNodeData(id, { in_name: newName })
    },
    [id, setNodes, canvas],
  )

  const handleEnterEditMode = useCallback(() => {
    setIsEditing(true)
    setEditSessionKey((k) => k + 1)
  }, [])

  const handleExitEditMode = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleCopyScript = useCallback(() => {
    const text = safeString(script)
    if (!text) return
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }, [script])

  // ── Background color ──

  const handleBgColorChange = useCallback(
    (colorKey: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_bg_color: colorKey } } : n,
        ),
      )
      canvas?.updateNodeData(id, { in_bg_color: colorKey })
      setShowColorPicker(false)
    },
    [id, setNodes, canvas],
  )

  useEffect(() => {
    if (!showColorPicker) return
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && e.target instanceof Node && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false)
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [showColorPicker])

  useEffect(() => {
    if (!selected) setShowColorPicker(false)
  }, [selected])

  // ── Toolbar ──

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'bg-color',
        label: 'Background Color',
        icon: <Palette size={14} />,
        onClick: () => setShowColorPicker((prev) => !prev),
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
        group: true,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: <CopyIcon />,
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <TrashIcon />,
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [handleDuplicate, handleDelete, handleAddToChat]
  )

  // ── Render helpers ──

  const resolvedProgressMessage = safeString(progressMessage) || 'Writing script...'

  const roleText = 'Write story scripts'

  const renderModelTabs = () => {
    if (!hasVariants || modelTabs.length === 0) return null

    return (
      <div className="h-9 px-4 border-b border-border-subtle flex items-center gap-1" data-testid="model-tabs">
        {modelTabs.map((tab) => {
          const isSelected = tab.key === currentActiveModel
          return (
            <button
              key={tab.key}
              disabled={tab.hasError || !tab.hasData}
              onClick={(e) => {
                e.stopPropagation()
                if (!tab.hasError && tab.hasData) handleTabClick(tab.key)
              }}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all nodrag',
                tab.hasError || !tab.hasData
                  ? 'text-text-muted/50 cursor-not-allowed'
                  : isSelected
                    ? 'bg-primary/15 text-primary border border-primary/30'
                    : 'text-text-muted hover:text-text-secondary hover:bg-surface-1'
              )}
              data-testid={`model-tab-${tab.key}`}
              data-active={isSelected}
            >
              <span
                className={cn(
                  'w-1.5 h-1.5 rounded-full flex-shrink-0',
                  tab.hasData && 'bg-success',
                  tab.hasError && 'bg-destructive',
                  !tab.hasData && !tab.hasError && 'bg-text-muted/30'
                )}
                data-testid={`model-status-${tab.key}`}
                data-status={tab.hasData ? 'completed' : tab.hasError ? 'error' : 'pending'}
              />
              {tab.label}
            </button>
          )
        })}
      </div>
    )
  }

  const renderEditButton = () => (
    <button
      onClick={(e) => {
        e.stopPropagation()
        handleEnterEditMode()
      }}
      title="Edit screenplay"
      className="w-6 h-6 rounded hover:bg-surface-1 text-text-muted hover:text-foreground flex items-center justify-center transition-colors nodrag"
      data-testid="screenplay-edit-btn"
    >
      <PencilIcon />
    </button>
  )

  const renderCopyButton = () => (
    <button
      onClick={(e) => {
        e.stopPropagation()
        handleCopyScript()
      }}
      title="Copy screenplay"
      className="w-6 h-6 rounded hover:bg-surface-1 text-text-muted hover:text-foreground flex items-center justify-center transition-colors nodrag"
      data-testid="screenplay-copy-btn"
      data-copied={copied ? 'true' : undefined}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  )

  const renderDoneButton = () => (
    <button
      onClick={(e) => {
        e.stopPropagation()
        handleExitEditMode()
      }}
      title="Done editing"
      className="w-6 h-6 rounded hover:bg-success/20 text-success hover:text-success flex items-center justify-center transition-colors nodrag"
      data-testid="screenplay-done-btn"
    >
      <CheckIcon />
    </button>
  )

  const renderScriptContent = () => {
    // Edit mode — RichTextBox
    if (isEditing && (activeScenes || script)) {
      return (
        <div className="w-full p-4 flex-1 min-h-0 flex flex-col nodrag nowheel" onWheel={stopWheelPropagation}>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xxs uppercase tracking-wider text-text-muted">
              Editing Screenplay
            </label>
            <div className="flex items-center gap-1">
              {renderCopyButton()}
              {renderDoneButton()}
            </div>
          </div>
          <RichTextBox
            key={editSessionKey}
            ref={richTextBoxRef}
            initialContent={safeString(script)}
            onContentChange={handleScriptChange}
            placeholder="Write your screenplay here..."
            flexFill
            maxLength={0}
            disabled={false}
            containerClassName="border border-border-subtle rounded-lg p-2 bg-surface-1 flex-1 min-h-0 nodrag nowheel"
          />
        </div>
      )
    }

    // Structured scenes — read-only SceneCard view
    if (activeScenes) {
      return (
        <div className="w-full p-4 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xxs uppercase tracking-wider text-text-muted">
              Screenplay — {activeScenes.length} scenes
            </label>
            <div className="flex items-center gap-1">
              {renderCopyButton()}
              {renderEditButton()}
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 nodrag nowheel space-y-3" onWheel={stopWheelPropagation}>
            {activeScenes.map((scene, i) => (
              <SceneCard key={i} scene={scene} voiceRolesById={voiceRolesById} />
            ))}
          </div>
        </div>
      )
    }

    // Plain text fallback — parse into scene cards if possible
    if (script) {
      if (fallbackScenes) {
        return (
          <div className="w-full p-4 flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xxs uppercase tracking-wider text-text-muted">
                Screenplay — {fallbackScenes.length} scenes
              </label>
              <div className="flex items-center gap-1">
                {renderCopyButton()}
                {renderEditButton()}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 nodrag nowheel space-y-3" onWheel={stopWheelPropagation}>
              {fallbackScenes.map((scene, i) => (
                <SceneCard key={i} scene={scene} voiceRolesById={voiceRolesById} />
              ))}
            </div>
          </div>
        )
      }
      return (
        <div className="w-full p-4 space-y-2 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between">
            <div className="text-xs text-text-muted">Script</div>
            <div className="flex items-center gap-2">
              {scriptPath && (
                <div className="text-xs text-success/60 font-mono truncate max-w-[200px]">
                  {safeString(scriptPath)}
                </div>
              )}
              {renderCopyButton()}
              {renderEditButton()}
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 nodrag nowheel" onWheel={stopWheelPropagation}>
            <pre className="text-[10px] text-text-secondary whitespace-pre-wrap leading-relaxed">
              {safeString(script)}
            </pre>
          </div>
        </div>
      )
    }

    // Empty state
    return (
      <div className="w-full py-6 flex flex-col items-center justify-center">
        <svg className="w-8 h-8 text-text-muted/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="text-xs text-text-muted/40 mt-1">No script yet</span>
      </div>
    )
  }

  const renderApprovalFooter = () => {
    // Script exists and is successful — show footer UI
    const hasScript = !!(activeScenes || script)
    if (status !== 'completed' || !hasScript) return null

    return (
      <div className="px-4 py-2.5 border-t border-border-subtle space-y-2" data-testid="script-approval-footer">
        {/* Refine toggle button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            setFeedbackMode((prev) => !prev)
          }}
          className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors nodrag"
          data-testid="refine-toggle-btn"
        >
          <PencilIcon />
          <span>{feedbackMode ? 'Hide feedback' : 'Refine'}</span>
        </button>

        {/* Feedback textarea */}
        {feedbackMode && (
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value.slice(0, CREATIVE_TEXT_MAX_LENGTH))}
            placeholder="Describe changes to the screenplay..."
            maxLength={CREATIVE_TEXT_MAX_LENGTH}
            rows={3}
            className={cn(
              'w-full px-3 py-2 text-xs bg-surface-1 border border-border-subtle rounded-lg',
              'text-foreground placeholder-text-muted/50',
              'focus:outline-none focus:border-primary/50 transition-colors resize-none',
              'nodrag nowheel'
            )}
            data-testid="refine-feedback-textarea"
          />
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {isRunning ? (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={isCancelling}
              className={cn(
                'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors nodrag',
                'border border-red-500/50 text-red-400',
                isCancelling
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-red-500/10 hover:text-red-300'
              )}
              data-testid="cancel-node-button"
            >
              {isCancelling ? 'Stopping...' : 'Stop'}
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleRegenerate()
              }}
              disabled={regenerateLocked}
              className={cn(
                'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors nodrag',
                'border border-border-subtle text-text-secondary',
                regenerateLocked
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-surface-1 hover:text-foreground'
              )}
              data-testid="regenerate-script-btn"
            >
              Regenerate
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Toolbar */}
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      {/* Floating label */}
      <FloatingNodeLabel name={nodeName || ''} fallback="Script Writer" typeIcon={NODE_TYPE_ICON.scriptWriter} onRename={handleRename} bgColor={data.in_bg_color && data.in_bg_color !== 'default' ? NODE_BG_SWATCH_COLORS[data.in_bg_color] : undefined} />

      {/* Color picker panel */}
      {showColorPicker && (
        <div
          ref={colorPickerRef}
          data-testid="script-writer-color-picker"
          style={{
            position: 'absolute',
            top: -52,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 60,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1.5 rounded-full bg-surface-2 backdrop-blur-md border border-border shadow-xl px-3 py-2">
            {Object.entries(NODE_BG_SWATCH_COLORS).map(([key, swatchColor]) => {
              const isActive = (data.in_bg_color ?? 'default') === key
              return (
                <button
                  key={key}
                  aria-label={NODE_BG_COLORS[key]?.label ?? key}
                  title={NODE_BG_COLORS[key]?.label ?? key}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleBgColorChange(key)
                  }}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: swatchColor,
                    border: isActive ? '2px solid white' : '2px solid transparent',
                    cursor: 'pointer',
                    flexShrink: 0,
                    outline: 'none',
                    boxShadow: isActive ? '0 0 0 1px rgba(255,255,255,0.3)' : 'none',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                />
              )
            })}
          </div>
        </div>
      )}

      {/* Card */}
      <div
        className={cn(
          'rounded-xl border transition-all duration-200 overflow-hidden cursor-pointer flex flex-col',
          (agentStatus === 'running' || agentStatus === 'pending_execution' || agentStatus === 'pending_cost_confirmation') && 'animate-node-shimmer',
        )}
        style={{ width: NODE_WIDTH, maxHeight: NODE_MAX_HEIGHT, backgroundColor: resolveNodeBgColor(data.in_bg_color), ...borderStyle }}
        onMouseEnter={() => {
          setIsHovered(true)
          setHoveredNodeId(id)
        }}
        onMouseLeave={() => {
          setIsHovered(false)
          setHoveredNodeId(null)
        }}
      >
        {/* Invisible handles */}
        <Handle type="target" position={Position.Top} id="in_upstream" className="!bg-transparent !border-0 !w-4 !h-1" />
        <Handle type="target" position={Position.Left} id="in_prompt" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_script" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_script_path" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_screenplay" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_dialogue" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_action_lines" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_characters" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_music_cues" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_sfx" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_gemini" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_claude" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Right} id="out_gpt" className="!bg-transparent !border-0 !w-1 !h-4" />
        <Handle type="source" position={Position.Bottom} id="bottom" className="!bg-transparent !border-0 !w-4 !h-1" />

        {/* Header */}
        <div className="px-4 py-3 border-b border-border-subtle">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 bg-surface-1 border"
              style={{ borderColor: agentColor.idle, color: agentColor.active }}
            >
              <ScriptIcon />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-xxs font-medium text-text-muted flex-shrink-0">
                  <span
                    className={cn(
                      'w-1.5 h-1.5 rounded-full',
                      agentStatus !== 'running' && indicator.color,
                      indicator.animate === 'pulse' && 'animate-pulse',
                      indicator.animate === 'spin' && 'animate-spin border border-t-transparent !rounded-full !bg-transparent',
                      indicator.animate === 'spin-slow' && 'animate-spin-slow border border-t-transparent !rounded-full !bg-transparent'
                    )}
                    style={
                      agentStatus === 'running'
                        ? { backgroundColor: agentColor.active }
                        : indicator.animate === 'spin' || indicator.animate === 'spin-slow'
                          ? { borderColor: 'var(--warning)', borderTopColor: 'transparent' }
                          : undefined
                    }
                  />
                  {indicator.label}
                </span>
              </div>
              <h3 className="text-base font-semibold text-text-primary truncate">{roleText}</h3>
            </div>
          </div>
        </div>

        {/* Model variant tabs */}
        {renderModelTabs()}

        {/* Content area */}
        {(agentStatus === 'running' || agentStatus === 'pending_execution' || agentStatus === 'pending_cost_confirmation') ? (
          <div className="bg-surface-1/50 flex-1 min-h-0 flex flex-col">
            {/* Compact spinner header */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border-subtle">
              <div
                className={`w-4 h-4 border-2 border-t-transparent rounded-full flex-shrink-0 ${
                  agentStatus === 'running' ? 'animate-spin' : agentStatus === 'pending_cost_confirmation' ? '' : 'animate-spin-slow'
                }`}
                style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
              />
              <span className="text-xxs text-text-muted flex-1">
                {agentStatus === 'running'
                  ? resolvedProgressMessage
                  : agentStatus === 'pending_cost_confirmation'
                    ? 'Waiting for cost approval in chat...'
                    : 'Queued — waiting for upstream nodes...'}
              </span>
            </div>
            {/* Progressive scene cards during streaming */}
            {agentStatus === 'running' && streamingScenes && streamingScenes.length > 0 ? (
              <div className="px-3 py-2 space-y-3 flex-1 min-h-0 overflow-y-auto nodrag nowheel" data-testid="streaming-scenes" onWheel={stopWheelPropagation}>
                {streamingScenes.map((scene, i) => (
                  <SceneCard
                    key={i}
                    scene={scene}
                    voiceRolesById={voiceRolesById}
                    isStreaming={i === streamingScenes.length - 1}
                  />
                ))}
              </div>
            ) : (
              <div className="px-4 py-4 flex items-center justify-center">
                <span className="text-xxs text-text-muted">
                  {agentStatus === 'running' ? 'Starting...' : ''}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="relative flex-1 min-h-0 flex flex-col">
            {/* Prompt display — folded by default with one-line preview */}
            {prompt && (
              <div className="border-b border-border-subtle">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowPrompt((prev) => !prev)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-surface-1 nodrag"
                  data-testid="script-prompt-toggle"
                  aria-expanded={showPrompt}
                >
                  <svg className="h-3 w-3 shrink-0 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                    <polyline points={showPrompt ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                  </svg>
                  <span className="shrink-0 text-xs text-text-muted">Prompt</span>
                  {!showPrompt && (
                    <span className="min-w-0 flex-1 truncate text-xs text-text-secondary">
                      {safeString(prompt)}
                    </span>
                  )}
                </button>
                {showPrompt && (
                  <div className="max-h-[120px] overflow-y-auto px-4 pb-3 pl-9 text-xs text-text-secondary nodrag nowheel" onWheel={stopWheelPropagation} data-testid="script-prompt-expanded">
                    {safeString(prompt)}
                  </div>
                )}
              </div>
            )}

            {/* Review history toggle */}
            {reviewHistory.length > 0 && (
              <div className="px-4 py-1.5 border-b border-border-subtle">
                <button
                  onClick={() => setShowReviewHistory(!showReviewHistory)}
                  className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors nodrag"
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points={showReviewHistory ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                  </svg>
                  <span>{showReviewHistory ? 'Hide' : 'Show'} review history ({reviewHistory.length})</span>
                  {reviewHistory.every((r) => r.accepted) ? (
                    <span className="text-xxs text-success/70 ml-auto">Accepted</span>
                  ) : (
                    <span className="text-xxs text-amber-500/70 ml-auto">
                      {reviewHistory.filter((r) => !r.accepted).length} revision
                      {reviewHistory.filter((r) => !r.accepted).length > 1 ? 's' : ''}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Review history panel */}
            {showReviewHistory && reviewHistory.length > 0 && (
              <div className="px-4 py-2 border-b border-border-subtle max-h-[200px] overflow-y-auto space-y-2 nodrag">
                {reviewHistory.map((review, i) => (
                  <div key={i} className="text-xs space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-text-secondary">Attempt {review.attempt}</span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-xxs font-medium ${
                          review.accepted
                            ? 'bg-success/15 text-success'
                            : 'bg-amber-500/15 text-amber-500'
                        }`}
                      >
                        {review.accepted ? 'Accepted' : 'Rejected'}
                      </span>
                    </div>
                    {review.feedback && (
                      <div className="text-text-muted pl-2 border-l-2 border-border-subtle">
                        {safeString(review.feedback).slice(0, 300)}
                        {safeString(review.feedback).length > 300 ? '...' : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Narrator / voice role summary */}
            <VoiceRolesPanel
              voiceRoles={activeVoiceRoles}
              voiceProfilesByName={voiceProfilesByName}
            />

            {/* Script content */}
            {renderScriptContent()}

            {/* Approval footer */}
            {renderApprovalFooter()}
          </div>
        )}

        {/* Error footer */}
        {error && (
          <div className="px-4 py-2 bg-destructive/10 border-t border-destructive/30">
            <span className="text-xxs text-destructive line-clamp-2 cursor-help" title={error}>
              {sanitizeNodeError(error)}
            </span>
          </div>
        )}
      </div>
    </div>
  )
})

ScriptWriterNode.displayName = 'ScriptWriterNode'
